/**
 * 首页（学习仪表盘）
 * ------------------------------------------------------------
 * 回答三个问题：
 *   1. 我现在的水平如何（等级/XP/今日进度）
 *   2. 我接下来该做什么（继续学习 / 待复习 / 推荐练习）
 *   3. 我的成长轨迹（成绩曲线 + 最近练习）
 */
import { state, levelFromXp } from '../core/store.js';
import { getUnitContent, listUnits, listTextbooks } from '../services/dataSource.js';
import { listSessions, todayStats, studyStreak, dueErrors, unitMasteryRate } from '../services/records.js';
import { levelInfo, achievementBoard } from '../services/rewards.js';
import { card, statRow, progressBar, lineChart, emptyState, tag } from '../components/ui.js';
import { navigate } from '../core/router.js';
import { escapeHtml, fmtDate, autoSpeak } from '../utils/index.js';
import { playWord, playSentenceEn } from '../services/audioService.js';

export default async function renderHome(mount) {
  const { textbookId, unitId } = state.selection;
  const lv = levelInfo();

  // 并行拉数据，减少等待
  const [today, streak, sessions, due, books] = await Promise.all([
    todayStats(), studyStreak(), listSessions({ limit: 8 }), dueErrors(20), listTextbooks(),
  ]);

  let unit = null;
  let units = [];
  let masteryRate = 0;
  if (textbookId) {
    units = await listUnits(textbookId);
    unit = units.find(u => u.id === unitId) || units[0] || null;
    if (unit) masteryRate = await unitMasteryRate(unit.id);
  }

  const book = books.find(b => b.id === textbookId);
  const goal = state.settings.dailyGoal || 30;
  const goalPct = Math.min(100, Math.round((today.total / goal) * 100));

  const trend = sessions.slice().reverse().map(s => ({ label: fmtDate(s.endedAt), value: s.score }));

  const hour = new Date().getHours();
  const greet = hour < 6 ? '夜深了' : hour < 11 ? '早上好' : hour < 14 ? '中午好' : hour < 18 ? '下午好' : '晚上好';

  mount.innerHTML = `
    <div class="page-head">
      <h1>${greet}，${escapeHtml(state.student?.name || '同学')} 👋</h1>
      <p class="lead">${unit
        ? `正在学习：${escapeHtml(book?.name || '')} · Unit ${unit.no} ${escapeHtml(unit.title || '')}`
        : '还没有选择教材，先去「教材管理」看看'}</p>
    </div>

    <div class="split">
      <div class="col" style="gap:16px">

        ${card(`
          <div class="row between" style="align-items:flex-start">
            <div>
              <div class="row" style="gap:10px;align-items:center">
                <span class="level-pill">Lv.${lv.level}</span>
                <strong style="font-size:15px">${lv.xp} XP</strong>
                <span class="muted">距离 Lv.${lv.level + 1} 还差 ${100 - lv.xpInLevel} XP</span>
              </div>
            </div>
            <div class="xp-bar" style="min-width:180px">${progressBar(lv.progress * 100)}</div>
          </div>
          <div class="stat-row" style="margin-top:18px">
            ${statRow([
              { label: '今日题量', value: today.total, unit: ' 题' },
              { label: '今日正确率', value: today.accuracy, unit: '%' },
              { label: '连续学习', value: streak, unit: ' 天' },
              { label: '待复习知识点', value: due.length, unit: ' 个' },
            ])}
          </div>
          <div style="margin-top:16px">
            <div class="row between" style="font-size:12px;margin-bottom:6px">
              <span class="muted">今日目标 ${today.total} / ${goal} 题</span>
              <span class="${goalPct >= 100 ? 'sym-ok' : 'muted'}">${goalPct >= 100 ? '目标达成 🎉' : goalPct + '%'}</span>
            </div>
            ${progressBar(goalPct, goalPct >= 100 ? 'green' : '')}
          </div>
        `, { title: '我的进度', extra: tag('每 100 XP 升一级', 'blue') })}

        ${unit ? card(`
          <div class="row between" style="align-items:flex-start;gap:16px;flex-wrap:wrap">
            <div style="min-width:200px">
              <div style="font-size:16px;font-weight:700;margin-bottom:4px">
                Unit ${unit.no} ${escapeHtml(unit.title || '')}
              </div>
              <div class="muted" style="font-size:12.5px">${escapeHtml(unit.theme || '本单元学习内容')}</div>
              <div class="mt-8">
                <span class="muted" style="font-size:12px">掌握率</span>
                <b style="margin-left:6px">${Math.round(masteryRate * 100)}%</b>
              </div>
            </div>
            <div class="grid c2" style="gap:8px;min-width:240px">
              <button class="btn btn-primary" data-go="practice" data-module="word">🔤 单词训练</button>
              <button class="btn btn-primary" data-go="practice" data-module="sentence">📝 句子训练</button>
              <button class="btn btn-ghost" data-go="practice" data-module="grammar">🧩 语法专项</button>
              <button class="btn btn-ghost" data-go="practice" data-module="comprehensive">📋 综合练习</button>
            </div>
          </div>
        `, { title: '继续学习' }) : card(emptyState('📚', '还没有教材', '先在「教材管理」查看内置教材，或用「导入中心」导入自己的 PDF',
          '<a class="btn btn-primary" href="#/textbooks">去教材管理</a>'), {})}

        ${card(
          `<div class="chart-card">${lineChart(trend)}</div>
           <div class="chart-legend"><span><i style="background:var(--c-primary)"></i>每次练习得分（满分 100）</span></div>`,
          { title: '进步曲线', extra: `<a class="btn btn-sm btn-ghost" href="#/report">查看全部</a>` }
        )}

      </div>

      <div class="col" style="gap:16px">
        ${card(
          due.length
            ? `<p class="muted" style="font-size:12.5px">这些知识点你最近出过错，优先复习效果最好。</p>
               <div class="review-list">
                 ${due.slice(0, 6).map(e => `
                   <div class="review-item">
                     <span class="sym-no">×</span>
                     <div class="grow">
                       <div style="font-size:13px">${escapeHtml(e.expected || e.prompt || '')}</div>
                       <div class="muted" style="font-size:11.5px">
                         ${e.refType === 'word' ? '单词' : e.refType === 'sentence' ? '句子' : '语法'} · L${e.level} · 错 ${e.wrongCount} 次
                       </div>
                     </div>
                     ${e.refType === 'word' && e.expected ? `<button class="icon-btn" data-en="${escapeHtml(e.expected)}" data-unit="${e.unitId || ''}" data-ref-type="${e.refType || 'word'}" data-ref-id="${escapeHtml(e.refId || '')}" type="button" title="发音">🔊</button>` : ''}
                   </div>`).join('')}
               </div>
               <a class="btn btn-primary btn-block mt-8" href="#/review">开始复习 (${due.length})</a>`
            : emptyState('✅', '暂无待复习内容', '做几道题后，错的地方会自动出现在这里'),
          { title: '待复习' }
        )}

        ${card(
          `<div class="quick-grid">
             <button class="quick-item" data-go="practice" data-module="word"><span>🔤</span><b>单词</b><small>7 级训练</small></button>
             <button class="quick-item" data-go="practice" data-module="sentence"><span>📝</span><b>句子</b><small>8 级递进</small></button>
             <button class="quick-item" data-go="games"><span>🎮</span><b>游戏</b><small>边玩边练</small></button>
             <button class="quick-item" data-go="review"><span>🔁</span><b>复习</b><small>错题优先</small></button>
           </div>`,
          { title: '快捷入口' }
        )}

        ${card(
          sessions.length
            ? `<ul class="mini-timeline">
                ${sessions.slice(0, 5).map(s => `
                  <li>
                    <span class="tl-dot"></span>
                    <div class="grow">
                      <div style="font-size:13px">${escapeHtml(s.title || moduleLabel(s.module))}</div>
                      <div class="muted" style="font-size:11.5px">${fmtDate(s.endedAt, true)} · ${s.total} 题</div>
                    </div>
                    <b class="${s.score >= 80 ? 'sym-ok' : s.score >= 60 ? 'sym-hint' : 'sym-no'}">${s.score}</b>
                  </li>`).join('')}
              </ul>`
            : emptyState('📊', '还没有练习记录', '完成第一次练习后这里会有时间线'),
          { title: '最近练习' }
        )}
      </div>
    </div>`;

  // ---- 交互 ----
  mount.querySelectorAll('[data-go]').forEach(btn => {
    btn.addEventListener('click', () => {
      const mod = btn.dataset.module;
      navigate(btn.dataset.go === 'practice' ? '#/practice' : `#/${btn.dataset.go}`, mod ? { module: mod } : {});
    });
  });
  mount.querySelectorAll('[data-speak]').forEach(btn => {
    btn.addEventListener('click', () => autoSpeak(btn.dataset.speak));
  });
  // 新版：优先用内置 MP3，没传 audio 才走 TTS
  mount.querySelectorAll('[data-en]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const en = btn.dataset.en;
      const unitId = btn.dataset.unit;
      const refType = btn.dataset.refType;
      const refId = btn.dataset.refId;
      let audio = '';
      if (unitId && refType === 'word') {
        try {
          const c = await getUnitContent({ textbookId: state.selection.textbookId, unitId });
          const w = (c.words || []).find(w => String(w.id) === refId || w.en === en);
          audio = w?.audio || '';
        } catch { /* 静默，没查到就 fallback */ }
      }
      if (audio) playWord({ en, audio });
      else if (refType === 'sentence') playSentenceEn(en);
      else playWord({ en });   // playWord 内部自动 fallback TTS
    });
  });
}

function moduleLabel(m) {
  return { word: '单词训练', sentence: '句子训练', grammar: '语法专项', comprehensive: '综合练习', game: '游戏', review: '复习' }[m] || '练习';
}
